import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { haptic } from '@/lib/haptics';

interface WelcomeScreenProps {
  onComplete: () => void;
}

export function WelcomeScreen({ onComplete }: WelcomeScreenProps) {
  const [step, setStep] = useState(0);

  const steps = [
    {
      icon: '📋',
      title: 'Track Your Inventory',
      description: 'Count your stock every day to keep accurate records and spot discrepancies early.'
    },
    {
      icon: '☀️🌙',
      title: 'Morning & Evening Counts',
      description: "Count when you open and when you close. We'll help you track what happened in between."
    },
    {
      icon: '📊',
      title: 'Spot Discrepancies',
      description: 'Compare your counts to sales records. Any difference might indicate issues.'
    },
  ];

  const handleNext = () => {
    haptic.light();
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      onComplete();
    }
  };

  const handleBack = () => {
    haptic.light();
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleSkip = () => {
    haptic.light();
    onComplete();
  };

  return (
    <div className="fixed inset-0 bg-dark z-50 flex flex-col">
      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <div className="text-8xl mb-8 animate-bounce">{steps[step].icon}</div>
        <h1 className="text-2xl font-black text-white mb-4">{steps[step].title}</h1>
        <p className="text-gray-400 max-w-xs">{steps[step].description}</p>
      </div>

      {/* Progress + Actions */}
      <div className="p-6 pb-safe">
        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-6">
          {steps.map((_, i) => (
            <div
              key={i}
              className={cn(
                'h-2 rounded-full transition-all',
                i === step ? 'w-6 bg-primary' : 'w-2 bg-gray-700'
              )}
            />
          ))}
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          {step > 0 && (
            <Button variant="ghost" onClick={handleBack} className="flex-1">
              Back
            </Button>
          )}
          <Button
            variant="primary"
            onClick={handleNext}
            className="flex-1"
          >
            {step < steps.length - 1 ? 'Next' : 'Get Started'}
          </Button>
        </div>

        {step === 0 && (
          <button
            onClick={handleSkip}
            className="w-full mt-4 text-gray-500 text-sm py-2 transition-all active:scale-[0.97]"
          >
            Skip intro
          </button>
        )}
      </div>
    </div>
  );
}
